import { apiService } from './api';
import { CheckpointSummary, LibraryCheckpoint, RiskAssessmentRow, RegulatoryNotice } from '../types';

/** Resolve document-local metadata references for assessment presentation. */
export function expandAssessment(payload: unknown): RiskAssessmentRow[] {
  if (Array.isArray(payload)) return payload as RiskAssessmentRow[];
  const document = payload as { rows: unknown[]; references: Record<string, unknown>; check_library: Record<string, unknown> };
  const fields = ['sources', 'edition_note', 'regulatory_source', 'obligations'];
  const resolve = (value: unknown): any => {
    if (Array.isArray(value)) return value.map(resolve);
    if (!value || typeof value !== 'object') return value;
    const input = value as Record<string, any>;
    const result: Record<string, any> = {};
    Object.entries(input).forEach(([key, item]) => {
      if (key.endsWith('_ref') && fields.includes(key.slice(0, -4))) result[key.slice(0, -4)] = document.references[item];
      else if (key !== 'check_ref') result[key] = resolve(item);
    });
    if (input.check_ref) {
      const definition = resolve(document.check_library[input.check_ref]);
      if (definition.risk_rating.library_check?.instructions_ref === true) {
        delete definition.risk_rating.library_check.instructions_ref;
        definition.risk_rating.library_check.instructions = definition.instructions;
      }
      result.instructions = definition.instructions;
      result.risk_rating = { ...definition.risk_rating, ...result.risk_rating };
    }
    return result;
  };
  return resolve(document.rows);
}

export const riskLibraryService = {
  checkSummary: (id: string) => apiService.get<{total:number;overdue:number;out_of_range:number;not_done:number;due_soon:number}>(`/facilities/${id}/check-summary`),
  notices: (id:string) => apiService.get<RegulatoryNotice[]>(`/risk-library/reports/${id}/notices`),
  assessment: (id: string) => apiService.get<unknown>(`/risk-library/facilities/${id}/assessment`).then(expandAssessment),
  applyFacility: (id: string) => apiService.post(`/risk-library/facilities/${id}/apply`),
  applyAsset: (id: string) => apiService.post(`/risk-library/assets/${id}/apply`),
  facilityCheckpoints: (id: string, dueOnly = false) => apiService.get<CheckpointSummary[]>(`/facilities/${id}/checkpoints`, {due_only: String(dueOnly)}),
  checkpoint: (id: string) => apiService.get<LibraryCheckpoint>(`/checkpoints/${id}`),
  checkpoints: (id: string) => apiService.get<LibraryCheckpoint[]>(`/assets/${id}/checkpoints`),
  record: (id: string, readings: Record<string, number>, submissionId: string) => apiService.post<{status:string;task_id?:string;finding_updated?:boolean}>(`/risk-library/checkpoints/${id}/measurements`, {readings,submission_id:submissionId}),
  async download(id: string) {
    const response = await fetch(`${apiService.getBaseUrl()}/risk-library/facilities/${id}/download`, { headers: await apiService.authHeaders() });
    if (!response.ok) throw new Error('Unable to download risk assessment');
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a'); link.href = url; link.download = decodeURIComponent(response.headers.get('Content-Disposition')?.split("filename*=UTF-8''")[1] || 'Risk_Assessment.docx'); link.click(); URL.revokeObjectURL(url);
  },
};
